import type { ForwardMode } from '@/constant/eventForwardSamples';
import type { ChargebeeEvent } from '@/types/events';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ForwardResult {
  ok: boolean;
  status: number;
  statusText: string;
  body: string;
  durationMs: number;
}

export interface ForwardError {
  message: string;
  /** True when the error is likely caused by missing CORS headers */
  isCorsLikely: boolean;
}

export interface ForwardConfig {
  mode: ForwardMode;
  proxyUrl: string;
  targetUrl: string;
}

// ─── Forward Event ──────────────────────────────────────────────────────────

/**
 * Forward a Chargebee event to a target URL, optionally through a proxy.
 * Returns either a result (with HTTP status + body) or an error.
 */
export async function forwardEvent(
  event: ChargebeeEvent,
  config: ForwardConfig
): Promise<{ result?: ForwardResult; error?: ForwardError }> {
  const { mode, proxyUrl, targetUrl } = config;

  const url =
    mode === 'proxy'
      ? `${proxyUrl.trim()}?target=${encodeURIComponent(targetUrl.trim())}`
      : targetUrl.trim();

  const start = performance.now();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    });

    const durationMs = Math.round(performance.now() - start);

    let body = '';
    try {
      body = await response.text();
      if (body.length > 1024) {
        body = `${body.slice(0, 1024)}…(truncated)`;
      }
      // Pretty-print JSON responses
      try {
        body = JSON.stringify(JSON.parse(body), null, 2);
      } catch {
        // Not JSON, keep as-is
      }
    } catch {
      body = '(unable to read response body)';
    }

    return {
      result: {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        body,
        durationMs,
      },
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to send request';
    const isCorsLikely =
      err instanceof TypeError &&
      (message.includes('Failed to fetch') || message.includes('NetworkError'));
    return { error: { message, isCorsLikely } };
  }
}
